/* eslint-disable no-nested-ternary */
import axios from 'axios';
import Response from '@/domain/response.entity';
import Pagination from '@/domain/pagination.entity';
import { alphanumericCnpj } from '@/shared/utils/cnpj';
import ContractGroup from '../entity/contract-group.entity';
import ContractGroupFilter from '../entity/contract-group-filter.entity';
import ContractGroupFilterBind from '../entity/contract-group-filter-bind.entity';
import CancelContractGroupRequest from '../request/cancel-contract-group.request';

export default class ContractGroupService {
  public static getAll(
    businessUnitId: string,
  ) {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-group`)
      .then((response: any) => ContractGroup.ofList(response.data));
  }

  public static filter(
    pagination: Pagination,
    businessUnitId: string,
    filter: ContractGroupFilter,
  ): Promise<Response> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/contract-group/filter`, {
        params: {
          page: pagination.page - 1,
          size: pagination.itemsPerPage,
          sortBy: pagination.arrangeSortBy,
          name: filter.name,
          cnpj: alphanumericCnpj(filter.cnpj),
          customerName: filter.customerName,
          contractCustomerName: filter.contractCustomerName,
          contractCnpj: alphanumericCnpj(filter.contractCnpj),
          contractExternalId: filter.contractExternalId,
          contractStatus: filter.contractStatus,
          contractBeneficiary: filter.contractBeneficiary,
        },
      })
      .then((response: any) => Response.of(response.data));
  }

  public static filterContractsToBind(
    pagination: Pagination,
    businessUnitId: string,
    filter: ContractGroupFilterBind,
  ): Promise<Response> {
    return axios
      .get(`/api/v1/contract-group/${businessUnitId}/contracts-to-bind`, {
        params: {
          page: pagination.page - 1,
          size: pagination.itemsPerPage,
          sortBy: pagination.arrangeSortBy,
          customerName: filter.customerName,
          cnpj: alphanumericCnpj(filter.cnpj),
          externalId: filter.externalId,
          status: filter.status,
          beneficiary: filter.beneficiary,
        },
      })
      .then((response: any) => Response.of(response.data));
  }

  public static hasNameContractGroup(
    businessUnitId: string,
    name: string,
    contractGroupId?: string,
  ): Promise<boolean> {
    const params = {
      params: {
        name,
        contractGroupId,
      },
    };

    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-group/validate-name`, params)
      .then((response: any) => response.data);
  }

  public static hasCnpjContractGroup(
    businessUnitId: string,
    cnpj: string,
  ): Promise<boolean> {
    cnpj = alphanumericCnpj(cnpj);
    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-group/validate-cnpj/${cnpj}`)
      .then((response: any) => response.data);
  }

  public static hasCnpjContractGroupEdit(
    businessUnitId: string,
    cnpj: string,
    contractGroupId: string,
  ): Promise<boolean> {
    cnpj = alphanumericCnpj(cnpj);
    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-group/validate-cnpj/${contractGroupId}/${cnpj}`)
      .then((response: any) => response.data);
  }

  public static createContractGroup(
    businessUnitId: string,
    contractId: string,
    name: string,
    customerName: string,
    cnpj: string,
  ): Promise<void> {
    cnpj = alphanumericCnpj(cnpj);
    const contractGroup = {
      contractId,
      name,
      customerName,
      cnpj,
    };
    return axios.post(`/api/v1/business-units/${businessUnitId}/contract-group`, contractGroup)
      .then((response: any) => response.data)
      .catch((error: any) => error.response.data);
  }

  public static editContractGroup(
    contractGroupId: string,
    name: string,
    customerName: string,
    cnpj: string,
  ): Promise<void> {
    cnpj = alphanumericCnpj(cnpj);
    const contractGroup = {
      name,
      customerName,
      cnpj,
    };
    return axios.put(`/api/v1/contract-group/${contractGroupId}/edit`, contractGroup)
      .then((response: any) => response.data)
      .catch((error: any) => error.response.data);
  }

  public static deleteContractGroup(
    contractGroupId: string,
  ): Promise<void> {
    return axios.delete(`/api/v1/contract-group/${contractGroupId}/delete`)
  }

  public static bindOrUnbindContractGroup(
    contractIds: string[],
    contractGroupId?: string,
  ): Promise<void> {
    return axios.patch('/api/v1/contract/contract-group/bind-unbind', { contractIds, contractGroupId });
  }

  public static getAllItensFromContractGroup(
    contractGroupId: string,
  ): Promise<any> {
    return axios.get(`/api/v1/contract-group/${contractGroupId}/contracts-to-cancel`)
      .then((response: any) => response.data);
  }

  public static cancelAllItensFromContractGroup(
    contractGroupId: string,
    itens: CancelContractGroupRequest,
  ): Promise<void> {
    return axios.put(`/api/v1/contract-group/${contractGroupId}/cancel`, itens)
      .then((response: any) => response.data);
  }

  public static getContractsFromContractGroup(
    contractGroupId: string,
  ): Promise<any> {
    return axios.get(`/api/v1/contract-group/${contractGroupId}/contracts`)
      .then((response: any) => response.data);
  }

  public static reopenAllItemsForContractGroup(
    contractGroupId: string,
    justificationText: string,
  ): Promise<any> {
    const body = { description: justificationText };
    return axios.post(`/api/v1/contract-group/${contractGroupId}/reopen`, body)
      .then((response: any) => response.data);
  }

  public static cancelExpirationRequestForGroup(
    contractGroupId: string,
    justificationId: string,
    observation?: string,
  ): Promise<void> {
    return axios.patch(`/api/v1/contract-group/${contractGroupId}/expire/cancel`, { justificationId, observation });
  }
}
